import React, {useMemo} from 'react';
import Button from '@adsk/bim360-matrix-react-button';
import SimpleModal from '@adsk/bim360-matrix-react-simple-modal';
import i18n from '../../utils/i18n';
import {ISSUE_ATTRIBUTE_NAMES} from '../../constants/issue-attribute-names';
import {Formik} from 'formik';
import {issueAttributesType} from '../../constants/proptype-validation';
import PropTypes from 'prop-types';
import ChangeIssueTypeModalContentContainer from './ChangeIssueTypeModalContentContainer';
import useChangeIssueTypeAffectedFields from './useChangeIssueTypeAffectedFields';
import {updateIssueAction} from '../../app/actions';
import {getIssueAttributesMappingEntitiesMap, getIssueTypesAdminEntitiesMap} from '../../app/selectors';
import {connect} from '../../app/connect';

const ChangeIssueTypeModal = ({
  container,
  issue,
  issueSubtypeId,
  onClose,
  fieldIssueTypeEntitiesMap,
  issueCustomAttributeMappings,
  dispatch
}) => {
  const pendingIssueSubtype = issueSubtypeId && fieldIssueTypeEntitiesMap[issueSubtypeId];
  const {getNewCustomAttributeMappings} = useChangeIssueTypeAffectedFields({
    fieldIssueTypeEntitiesMap,
    issueCustomAttributeMappings,
    issue
  });

  const customAttributeInitialValues = useMemo(
    () =>
      Object.fromEntries(
        (issueSubtypeId ? getNewCustomAttributeMappings(issueSubtypeId) : []).map(({attributeDefinitionId}) => [
          attributeDefinitionId,
          ''
        ])
      ),
    [getNewCustomAttributeMappings, issueSubtypeId]
  );

  const initialValues = {
    [ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID]: issueSubtypeId,
    [ISSUE_ATTRIBUTE_NAMES.ISSUE_TYPE_ID]: pendingIssueSubtype?.parentId,
    [ISSUE_ATTRIBUTE_NAMES.CUSTOM_ATTRIBUTES]: customAttributeInitialValues
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={({[ISSUE_ATTRIBUTE_NAMES.CUSTOM_ATTRIBUTES]: customAttributes, ...attributes}) => {
        dispatch(
          updateIssueAction(
            issue,
            {
              ...attributes,
              [ISSUE_ATTRIBUTE_NAMES.CUSTOM_ATTRIBUTES]: Object.entries(customAttributes).map(
                ([attributeDefinitionId, value]) => ({
                  attributeDefinitionId,
                  value
                })
              )
            },
            {
              onSuccess: onClose,
              onError: onClose
            }
          )
        );
      }}
    >
      {({handleSubmit, isSubmitting}) => (
        <SimpleModal
          show={!!issueSubtypeId}
          container={container}
          title={i18n.t('preview.changeTypeModal.title')}
          modalBodyProps={{
            className: 'ChangeIssueTypeModal__body'
          }}
          footer={<div>
            <Button styleType={Button.StyleType.LINK_SECONDARY} onClick={onClose}>
              {i18n.t('navigation.cancel')}
            </Button>
            <Button styleType={Button.StyleType.PRIMARY} disabled={isSubmitting} onClick={handleSubmit}>
              {isSubmitting ? i18n.t('navigation.saving') : i18n.t('navigation.saveChanges')}
            </Button>
          </div>}
          onExited={onClose}
        >
          {issueSubtypeId && <ChangeIssueTypeModalContentContainer issueSubtypeId={issueSubtypeId} issue={issue} />}
        </SimpleModal>
      )}
    </Formik>
  );
};

ChangeIssueTypeModal.displayName = 'ChangeIssueTypeModal';

ChangeIssueTypeModal.propTypes = {
  container: PropTypes.object,
  issue: issueAttributesType.isRequired,
  issueSubtypeId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  fieldIssueTypeEntitiesMap: PropTypes.object.isRequired,
  issueCustomAttributeMappings: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  fieldIssueTypeEntitiesMap: getIssueTypesAdminEntitiesMap(state),
  issueCustomAttributeMappings: getIssueAttributesMappingEntitiesMap(state)
});

export default connect(mapStateToProps)(ChangeIssueTypeModal);
